import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Users, Target, ArrowRight, MessageSquare } from 'lucide-react';
import '../styles/agentPage.css';
import '../styles/audienceAgent.css';

function AudienceAgent() {
  const { user, isAuthenticated, refreshToken } = useAuth();
  const navigate = useNavigate();
  const [chat, setChat] = useState([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [audienceConfirmed, setAudienceConfirmed] = useState(false);
  const [lastAudience, setLastAudience] = useState(null);
  const chatWindowRef = useRef(null);
  
  const formatMessage = useCallback((content) => {
    return content
      // Headers
      .replace(/^### (.*$)/gm, '<h3>$1</h3>')
      .replace(/^## (.*$)/gm, '<h2>$1</h2>')
      .replace(/^# (.*$)/gm, '<h1>$1</h1>')
      
      // Bold and italic
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      
      // Fix for lists - this is the important part
      .replace(/^- (.*?)$/gm, '<li>$1</li>')
      .replace(/(<li>.*?<\/li>\n)+/g, '<ul>$&</ul>')
      
      // Links and line breaks
      .replace(/(\w+)\[(https?:\/\/[^\]]+)\]/g, '<a href="$2" target="_blank">$1</a>')
      .replace(/\n\n/g, '<br/><br/>')
      .replace(/\n/g, '<br/>');
  }, []);

  const getWelcomeMessage = useCallback(async () => {
    try {
      await refreshToken();
      const result = await axios.post(
        'https://n8n.iniai.cloud/webhook/ed-audience-agent',
        {
          type: 'welcome',
          user: {
            id: user?.id || '',
            email: user?.email || '',
            role: user?.role || '',
            name: user?.name || ''
          },
          sessionId: `${user?.id || 'anonymous'}-${Date.now()}`
        },
        {
          headers: {
            'x-api-key': process.env.REACT_APP_CREATE_USER_API_KEY,
            Authorization: `Bearer ${user?.token || ''}`
          }
        }
      );

      if (Array.isArray(result.data) && result.data.length > 0) {
        const response = result.data[0].output || result.data[0].response;
        if (response) {
          setChat([{ 
            type: 'agent', 
            content: formatMessage(response)
          }]);
        }
      }
    } catch (error) {
      console.error('Error fetching welcome message:', error);
      setChat([{
        type: 'agent',
        content: `Welcome to the Audience Sizing Agent. How can I help you define your target audience today?`
      }]);
    }
  }, [user, isAuthenticated, refreshToken, formatMessage]);

  useEffect(() => {
    if (isAuthenticated && user && chat.length === 0) {
      getWelcomeMessage();
    }
  }, [isAuthenticated, user, chat.length, getWelcomeMessage]);

  useEffect(() => {
    if (chatWindowRef.current) {
      requestAnimationFrame(() => {
        chatWindowRef.current.scrollTop = chatWindowRef.current.scrollHeight;
      });
    }
  }, [chat]);

  // Check for audience data in the agent's response
  useEffect(() => {
    if (chat.length > 0) {
      const lastMessage = chat[chat.length - 1];
      if (lastMessage.type === 'agent') {
        // Look for audience data in the message
        // This is a simplified detection - in a real app, you might have a more structured way to detect audience confirmation
        const content = lastMessage.content.toLowerCase();
        if (content.includes('audience size:') || content.includes('audience range:')) {
          // Extract audience name using regex
          const nameMatch = lastMessage.content.match(/audience name: ([^<]+)/i);
          const sizeMatch = lastMessage.content.match(/audience size: ([0-9,]+)/i);
          const marketMatch = lastMessage.content.match(/audience market: ([^<]+)/i);
          
          if (nameMatch && sizeMatch) {
            setAudienceConfirmed(true);
            setLastAudience({
              name: nameMatch[1].trim(),
              size: sizeMatch[1].trim(),
              market: marketMatch ? marketMatch[1].trim() : 'Global'
            });
          }
        }
      }
    }
  }, [chat]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;
  
    const userMessage = { type: 'user', content: query };
    setChat(prevChat => [...prevChat, userMessage]);
    const currentQuery = query;
    setQuery('');
    setLoading(true);
  
    try {
      await refreshToken();
      const result = await axios.post(
        'https://n8n.iniai.cloud/webhook/ed-audience-agent',
        {
          type: 'query',
          query: currentQuery,
          user: {
            id: user?.id || '',
            email: user?.email || '',
            role: user?.role || '',
            name: user?.name || ''
          },
          sessionId: `${user?.id || 'anonymous'}-${Date.now()}`
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': process.env.REACT_APP_CREATE_USER_API_KEY,
          }
        }
      );
  
      if (Array.isArray(result.data) && result.data.length > 0) {
        const formattedContent = formatMessage(result.data[0].response || result.data[0].output);
        setChat(prevChat => [...prevChat, { type: 'agent', content: formattedContent }]);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'An error occurred while processing your request.';
      setChat(prevChat => [...prevChat, { type: 'agent', content: errorMessage }]);
    } finally {
      setLoading(false);
    }
  };

  const handleContinueToChannel = () => {
    // Store audience info in sessionStorage for the next step
    if (lastAudience) {
      sessionStorage.setItem('currentAudience', JSON.stringify(lastAudience));
      navigate('/agent/channel');
    }
  };

  const handleContinueToAffinity = () => {
    // Store audience info in sessionStorage for the next step
    if (lastAudience) {
      sessionStorage.setItem('currentAudience', JSON.stringify(lastAudience));
      navigate('/agent/affinity');
    }
  };

  const handleSkipToPlanner = () => {
    // Store audience info in sessionStorage for the next step
    if (lastAudience) {
      sessionStorage.setItem('currentAudience', JSON.stringify(lastAudience));
      navigate('/reachcurve/planner');
    }
  };

  return (
    <div className="agent-container">
      <div className="agent-header">
        <div className="agent-title">
          <Users className="agent-icon" />
          <h1>Ed: Audience Sizing Agent</h1>
        </div>
        <p className="agent-description">
          Ed helps you define and size your target audience with precision using probabilistic audience sizing methods.
        </p>
      </div>

      {audienceConfirmed && (
        <div className="audience-confirmed">
          <div className="audience-details">
            <Target className="check-icon" />
            <h3>Audience Confirmed</h3>
            <p><strong>Name:</strong> {lastAudience?.name}</p>
            <p><strong>Size:</strong> {lastAudience?.size}</p>
            <p><strong>Market:</strong> {lastAudience?.market}</p>
          </div>
          <div className="next-steps">
            <h4>What would you like to do next?</h4>
            <div className="option-buttons">
              <button className="option-button" onClick={handleContinueToChannel}>
                <MessageSquare className="option-icon" />
                <span>Channel Pricing Data</span>
                <ArrowRight className="arrow-icon" />
              </button>
              <button className="option-button" onClick={handleContinueToAffinity}>
                <MessageSquare className="option-icon" />
                <span>Audience Affinities</span>
                <ArrowRight className="arrow-icon" />
              </button>
              <button className="option-button skip" onClick={handleSkipToPlanner}>
                <Target className="option-icon" />
                <span>Skip to Reach Curve</span>
                <ArrowRight className="arrow-icon" />
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="chat-window" ref={chatWindowRef}>
        {chat.map((message, index) => (
          <div
            key={index}
            className={`message-bubble ${message.type === 'user' ? 'user' : 'agent'}`}
          >
            <div 
              dangerouslySetInnerHTML={{ __html: message.content }} 
              className="message-content"
            />
          </div>
        ))}

        {loading && (
          <div className="message-bubble agent">
            <p className="loading-text">
              Ed is analyzing your audience query
              <span className="dots">...</span>
            </p>
          </div>
        )}
      </div>

      <form className="chat-input" onSubmit={handleSubmit}>
        <textarea
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Describe your target audience (e.g., UK adults aged 30-50 with an interest in sustainable fashion)..."
          rows="3"
        />
        <button type="submit" disabled={loading}>
          {loading ? 'Processing...' : 'Send'}
        </button>
      </form>
    </div>
  );
}

export default AudienceAgent;